"use client";
import { useEffect, useMemo, useState } from "react";
import { filter as lodashFilter, orderBy } from "lodash";
import PerformerService, { Performer } from "@/app/_services/PerformerService";
import { toast } from "@/app/_utils/toast";

export function usePerformersModel() {
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedPerformer, setSelectedPerformer] = useState<Performer | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [showsPerformer, setShowsPerformer] = useState<Performer | null>(null);

  async function loadPerformers() {
    setIsLoading(true);
    try {
      const res = await PerformerService.getPerformersAsync();
      setPerformers(orderBy(res, ["created_at"], ["desc"]));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadPerformers();
  }, []);

  const filteredPerformers = useMemo(() => {
    if (!search.trim()) return performers;
    const q = search.trim().toLowerCase();
    return lodashFilter(
      performers,
      (p) =>
        p.first_name?.toLowerCase().includes(q) || p.last_name?.toLowerCase().includes(q)
    );
  }, [performers, search]);

  function openCreateForm() {
    setSelectedPerformer(null);
    setOpenForm(true);
  }

  function openEditForm(performer: Performer) {
    setSelectedPerformer(performer);
    setOpenForm(true);
  }

  function closeForm() {
    setOpenForm(false);
  }

  async function handleDelete(performer: Performer) {
    try {
      await PerformerService.deletePerformerAsync(performer.id);
      toast.success("Performer deleted");
      await loadPerformers();
    } catch (e) {
      toast.error("Failed to delete performer");
    }
  }

  return {
    filteredPerformers,
    isLoading,
    search,
    setSearch,
    selectedPerformer,
    openForm,
    openCreateForm,
    openEditForm,
    closeForm,
    handleDelete,
    showsPerformer,
    openShows: setShowsPerformer,
    closeShows: () => setShowsPerformer(null),
    refetch: loadPerformers,
  };
}
